import { useState, useEffect, useMemo } from "react";

/**
 * Shared hook: paginate an array and rotate page every intervalMs.
 * @param {Array} items - Full list
 * @param {number} perPage - Items per page
 * @param {number} intervalMs - Rotation interval in ms
 * @returns {{ pageIndex: number, pageCount: number, visibleItems: Array }}
 */
export function usePaging(items, perPage, intervalMs) {
  const per = Math.max(1, Number(perPage) || 1);
  const pageCount = Math.max(1, Math.ceil((items || []).length / per));
  const [pageIndex, setPageIndex] = useState(0);

  const visibleItems = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const start = pageIndex * per;
    return list.slice(start, start + per);
  }, [items, pageIndex, per]);

  useEffect(() => {
    setPageIndex((i) => (pageCount <= 1 ? 0 : Math.min(i, pageCount - 1)));
  }, [per, pageCount]);

  useEffect(() => {
    if (pageCount <= 1) return undefined;
    const id = setInterval(() => {
      setPageIndex((i) => (i + 1) % pageCount);
    }, intervalMs);
    return () => clearInterval(id);
  }, [pageCount, intervalMs]);

  return { pageIndex, pageCount, visibleItems };
}

export default usePaging;
